export type AuthOutcomeErrorKind =
	| "invalid_credentials"
	| "user_already_exists"
	| "invalid_or_expired"
	| "email_not_verified"
	| "rate_limited"
	| "network"
	| "missing_token"
	| "unknown";

export function readAuthClientErrorCode(err: unknown): string | undefined {
	if (!err || typeof err !== "object") {
		return undefined;
	}
	const code = (err as Record<string, unknown>).code;
	return typeof code === "string" ? code : undefined;
}

export function readAuthClientErrorMessage(err: unknown): string {
	if (err instanceof Error) {
		return err.message;
	}
	if (err && typeof err === "object") {
		const rec = err as Record<string, unknown>;
		if (typeof rec.message === "string") {
			return rec.message;
		}
	}
	return String(err ?? "");
}

export function classifyAuthOutcomeError(err: unknown): {
	error_kind: AuthOutcomeErrorKind;
	orpc_code?: string;
} {
	const orpcCode = readAuthClientErrorCode(err);
	const raw = readAuthClientErrorMessage(err);
	const lower = raw.toLowerCase();
	const code = orpcCode ?? "";

	if (
		lower.includes("failed to fetch") ||
		lower.includes("networkerror") ||
		lower.includes("load failed")
	) {
		return { error_kind: "network", orpc_code: orpcCode };
	}

	if (
		code === "TOO_MANY_REQUESTS" ||
		lower.includes("rate_limit") ||
		lower.includes("too many requests")
	) {
		return { error_kind: "rate_limited", orpc_code: orpcCode };
	}

	if (
		code === "INVALID_EMAIL_OR_PASSWORD" ||
		code === "INVALID_PASSWORD" ||
		code === "USER_NOT_FOUND" ||
		code === "CREDENTIAL_ACCOUNT_NOT_FOUND"
	) {
		return { error_kind: "invalid_credentials", orpc_code: orpcCode };
	}

	if (
		code === "USER_ALREADY_EXISTS" ||
		code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL"
	) {
		return { error_kind: "user_already_exists", orpc_code: orpcCode };
	}

	if (code === "EMAIL_NOT_VERIFIED") {
		return { error_kind: "email_not_verified", orpc_code: orpcCode };
	}

	if (
		code === "INVALID_TOKEN" ||
		code === "INVALID_INVITATION" ||
		code === "SESSION_EXPIRED" ||
		lower.includes("not_found")
	) {
		return { error_kind: "invalid_or_expired", orpc_code: orpcCode };
	}

	return { error_kind: "unknown", orpc_code: orpcCode };
}
